import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { MaterialModule } from 'src/app/material.module';
import { AbdmConsentRequestDialogComponent } from './abdm-consent-request-dialog.component';
import { AbdmFhirView, formatFhirJson, presentFhirBundle } from './abdm-fhir-presenter';
import { AbdmHealthRecordsService } from './abdm-health-records.service';
import { AbdmHiuConsent, AbdmHiuRecord } from './abdm-health-records.type';
import { CommonDatePipe } from 'src/app/pipe/common-date.pipe';

@Component({
  selector: 'app-abdm-health-records',
  standalone: true,
  imports: [CommonModule, MaterialModule, TranslateModule, CommonDatePipe],
  templateUrl: './abdm-health-records.component.html',
  styleUrl: './abdm-health-records.component.scss'
})
export class AbdmHealthRecordsComponent implements OnInit, OnDestroy {
  @Input({ required: true }) patientId!: number;
  consents: AbdmHiuConsent[] = [];
  records: AbdmHiuRecord[] = [];
  clinicalViews = new Map<number, AbdmFhirView>();
  loading = false;
  pendingFetch = false;
  searchTerm = '';
  currentPage = 1;
  readonly pageSize = 10;

  private readonly destroy$ = new Subject<void>();
  private readonly recoveryInFlight = new Set<number>();

  constructor(
    private service: AbdmHealthRecordsService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.refresh();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get filteredRecords(): AbdmHiuRecord[] {
    const search = this.searchTerm.trim().toLowerCase();
    if (!search) {
      return this.records;
    }

    return this.records.filter(record => [
      record.title,
      record.hiType,
      record.sourceHipId,
      record.careContextReference
    ].some(value => value?.toLowerCase().includes(search)));
  }

  get pagedRecords(): AbdmHiuRecord[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredRecords.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredRecords.length / this.pageSize));
  }

  get visiblePages(): number[] {
    const start = Math.max(1, Math.min(this.currentPage - 1, this.totalPages - 3));
    const end = Math.min(this.totalPages, start + 3);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  get firstVisibleRecord(): number {
    return this.filteredRecords.length ? (this.currentPage - 1) * this.pageSize + 1 : 0;
  }

  get lastVisibleRecord(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredRecords.length);
  }

  refresh(): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    forkJoin({
      consents: this.service.getConsents(this.patientId),
      records: this.service.getRecords(this.patientId)
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        this.consents = response.consents.result || [];
        this.records = response.records.result || [];
        this.currentPage = 1;
        this.loading = false;
        this.pendingFetch = this.consents.some(consent => this.isPending(consent));
      },
      error: (error) => {
        this.loading = false;

        const message =
          error?.error?.message ||
          error?.error?.errors?.[0] ||
          this.translate.instant('ABDM.REFRESH_FAILED');

        this.toastr.error(message);
      }
    });
  }

  requestRecords(): void {
    this.dialog.open(AbdmConsentRequestDialogComponent, {
      width: '560px',
      data: { patientId: this.patientId }
    }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe(created => {
      if (created) {
        this.refresh();
      }
    });
  }

  updateSearch(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
    this.currentPage = 1;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
  }

  statusKey(status: string): string {
    return `ABDM.STATUS.${status.replace(/[^a-zA-Z]/g, '_').toUpperCase()}`;
  }

  statusLabel(status: string): string {
    const key = this.statusKey(status);
    const translated = this.translate.instant(key);
    return translated === key
      ? status.replace(/([a-z])([A-Z])/g, '$1 $2')
      : translated;
  }
  statusClass(status: string): string {
    return status
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/[^a-zA-Z]+/g, '-')
      .toLowerCase();
  }

  getStatusIcon(status: string): string {
    const s = status.toLowerCase();
    if (s.includes('granted') || s.includes('complete')) {
      return 'check_circle';
    }
    if (s.includes('fail') || s.includes('deny') || s.includes('reject') || s.includes('revoke')) {
      return 'cancel';
    }
    return 'schedule';
  }

  recordIcon(hiType: string): string {
    const type = hiType.replace(/[\s_-]/g, '').toLowerCase();
    if (type.includes('discharge')) {
      return 'description';
    }
    if (type.includes('prescription')) {
      return 'medication';
    }
    if (type.includes('diagnostic')) {
      return 'clinical_notes';
    }
    return 'stethoscope';
  }

  retry(consent: AbdmHiuConsent): void {
    if (!this.beginRecovery(consent.id)) {
      return;
    }
    this.service.retryConsent(consent.id).pipe(
      finalize(() => this.endRecovery(consent.id)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('ABDM.RETRY_SUCCESS'));
        this.refresh();
      },
      error: () => this.toastr.error(this.translate.instant('ABDM.RETRY_FAILED'))
    });
  }

  canCheckStatus(consent: AbdmHiuConsent): boolean {
    return ['Requested', 'InitSent'].includes(consent.status) && !!consent.consentRequestId;
  }

  canRetryFetch(consent: AbdmHiuConsent): boolean {
    return consent.status === 'Granted'
      && ['NotStarted', 'Failed'].includes(consent.dataFetchStatus);
  }

  isRecovering(consentId: number): boolean {
    return this.recoveryInFlight.has(consentId);
  }

  checkConsentStatus(consent: AbdmHiuConsent): void {
    if (!this.beginRecovery(consent.id)) {
      return;
    }
    this.service.checkConsentStatus(consent.id).pipe(
      finalize(() => this.endRecovery(consent.id)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: response => {
        if (response.result) {
          Object.assign(consent, response.result);
        }
        this.refresh();
      },
      error: () => this.toastr.error(this.translate.instant('ABDM.STATUS_CHECK_FAILED'))
    });
  }

  retryFetch(consent: AbdmHiuConsent): void {
    if (!this.beginRecovery(consent.id)) {
      return;
    }
    this.pendingFetch = true;
    this.service.fetchConsent(consent.id).pipe(
      finalize(() => this.endRecovery(consent.id)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: response => {
        if (response.result?.dataFetchStatus === 'Failed') {
          this.toastr.error(this.translate.instant('ABDM.FETCH_FAILED'));
        }
        this.refresh();
      },
      error: () => {
        this.pendingFetch = false;
        this.toastr.error(this.translate.instant('ABDM.FETCH_FAILED'));
      }
    });
  }

  private beginRecovery(consentId: number): boolean {
    if (this.recoveryInFlight.has(consentId)) {
      return false;
    }
    this.recoveryInFlight.add(consentId);
    return true;
  }

  private endRecovery(consentId: number): void {
    this.recoveryInFlight.delete(consentId);
  }

  private isPending(consent: AbdmHiuConsent): boolean {
    if (['Requested', 'InitSent', 'FetchPending'].includes(consent.status)) {
      return true;
    }
    return consent.status === 'Granted' && ['NotStarted', 'Pending'].includes(consent.dataFetchStatus);
  }

  loadDetails(record: AbdmHiuRecord): void {
    if (record.fhirJson) {
      this.attachClinicalView(record);
      return;
    }
    this.service.getRecord(record.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        Object.assign(record, response.result);
        this.attachClinicalView(record);
      },
      error: () => this.toastr.error(this.translate.instant('ABDM.DETAIL_LOAD_FAILED'))
    });
  }

  parseSummary(summary: string): string {
    try {
      const value = JSON.parse(summary);
      const resourceLabel = this.translate.instant('ABDM.FHIR_RESOURCES_COUNT', { count: value.resourceCount });
      return [value.title, resourceLabel, ...(value.highlights || [])].filter(Boolean).join(' | ');
    }
    catch { return summary; }
  }

  formatFhir(fhirJson: string): string {
    return formatFhirJson(fhirJson);
  }

  private attachClinicalView(record: AbdmHiuRecord): void {
    if (!record.fhirJson || this.clinicalViews.has(record.id)) {
      return;
    }
    const view = presentFhirBundle(record.fhirJson);
    if (view) {
      this.clinicalViews.set(record.id, view);
    }
  }
}
